'use client';

import { useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { toast } from 'sonner';
import { AssistantAvatar } from './assistant-avatar';
import { ArrowRight, ArrowUpRight } from '@phosphor-icons/react';
import { loadRouletteBox } from '@/components/storefront/roulette-catalog-card';
import { isRouletteOffered, useRouletteConfig } from '@/hooks/use-roulette-config';
import './order-assistant-entry.css';

type RouletteBox = Awaited<ReturnType<typeof loadRouletteBox>>;

const AssistantDialog = dynamic(
  () => import('./order-assistant-dialog').then((module) => module.OrderAssistantDialog),
  { ssr: false }
);

export const assistantEntryVariants = ['companion', 'ribbon', 'occasion', 'editorial'] as const;
export type AssistantEntryVariant = (typeof assistantEntryVariants)[number];

function EntryContent({ variant }: { variant: AssistantEntryVariant }) {
  if (variant === 'companion') {
    return (
      <>
        <span className="order-entry-avatar-slot" aria-hidden="true">
          <AssistantAvatar size={76} className="order-entry-avatar" />
        </span>
        <span className="order-entry-copy">
          <strong className="order-entry-title">Что закажем сегодня?</strong>
          <span className="order-entry-description">Помогу выбрать по вкусу и бюджету.</span>
        </span>
        <span className="order-entry-action">
          <span className="order-entry-action-label">Подобрать заказ</span>
          <span className="order-entry-arrow">
            <ArrowUpRight size={21} weight="bold" aria-hidden="true" />
          </span>
        </span>
      </>
    );
  }
  if (variant === 'ribbon') {
    return (
      <>
        <span className="order-entry-ribbon-scene" aria-hidden="true" />
        <span className="order-entry-copy">
          <strong className="order-entry-title order-entry-ribbon-title">
            <span>На одного?</span>
            <span>На двоих?</span>
            <span>На всех.</span>
          </strong>
          <span className="order-entry-description">Вкусный план — на любой случай.</span>
        </span>
        <span className="order-entry-action">
          <span className="order-entry-action-label">Начать подбор</span>
          <ArrowUpRight size={21} weight="bold" aria-hidden="true" />
        </span>
      </>
    );
  }
  if (variant === 'occasion') {
    return (
      <>
        <span className="order-entry-copy">
          <strong className="order-entry-title">Под ваши планы.</strong>
          <span className="order-entry-description">Найдём то самое сочетание.</span>
        </span>
        <span className="order-entry-occasions" aria-hidden="true">
          <span>Для себя</span>
          <span>На двоих</span>
          <span>На компанию</span>
        </span>
        <span className="order-entry-action">
          <span className="order-entry-action-label">Подобрать</span>
          <ArrowRight size={23} aria-hidden="true" />
        </span>
      </>
    );
  }
  return (
    <>
      <span className="order-entry-number" aria-hidden="true">
        04
      </span>
      <span className="order-entry-copy">
        <strong className="order-entry-title">
          Четыре вопроса.
          <br />
          <span>Ваше сочетание.</span>
        </strong>
        <span className="order-entry-description">Соберём заказ вместе.</span>
      </span>
      <span className="order-entry-action">
        <span className="order-entry-action-label">Начать</span>
        <span className="order-entry-arrow">
          <ArrowUpRight size={25} aria-hidden="true" />
        </span>
      </span>
    </>
  );
}

export function OrderAssistantEntry({ variant }: { variant?: AssistantEntryVariant } = {}) {
  const [open, setOpen] = useState(false);
  const [Box, setBox] = useState<RouletteBox | null>(null);
  const [boxOpen, setBoxOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const boxReturnFocus = useRef<(() => void) | null>(null);
  const rouletteConfig = useRouletteConfig().data;
  const selected = variant ?? 'companion';
  // Коробка открывается отсюда, а не карточкой каталога: так она работает,
  // даже если карточки рулетки на странице нет. Помощник остаётся под ней —
  // подборка не теряется, после коробки человек снова в чате.
  const openRoulette = async (returnFocus: () => void) => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const component = await Promise.race([
        loadRouletteBox(),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error('Roulette box load timed out')), 10000);
        }),
      ]);
      boxReturnFocus.current = returnFocus;
      setBox(() => component);
      setBoxOpen(true);
    } catch {
      toast.error('Не удалось открыть коробку. Попробуйте ещё раз.');
    } finally {
      clearTimeout(timeout);
    }
  };
  return (
    <>
      <div className="order-helper-entry-container">
        <button
          ref={button}
          className={`order-help-entry order-helper-invitation order-entry-${selected}`}
          data-order-assistant="open"
          data-assistant-style={selected}
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <EntryContent variant={selected} />
        </button>
      </div>
      {open && (
        <AssistantDialog
          onClose={() => setOpen(false)}
          onOpenRoulette={isRouletteOffered(rouletteConfig) ? openRoulette : undefined}
        />
      )}
      {Box && boxOpen && isRouletteOffered(rouletteConfig) && (
        <Box
          open={boxOpen}
          onOpenChange={setBoxOpen}
          config={rouletteConfig}
          onReturnFocus={() => {
            const returnFocus = boxReturnFocus.current;
            boxReturnFocus.current = null;
            // Помощник могли закрыть, пока коробка грузилась, — тогда к кнопке входа.
            if (returnFocus && open) returnFocus();
            else button.current?.focus({ preventScroll: true });
          }}
        />
      )}
    </>
  );
}
